import { sendEvent } from '@JohnLewisPartnership/experiments-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { dispatchNewEvent } from '../../../utils/analytics/dispatchNewEvent';
import {
  isMin1280pxWide,
  min1280pxWideMediaQuery,
} from '../../../utils/device/identifyDevice';

type UseMobileMenuToggleParams = {
  updateMobileNavState: (isOpen: boolean) => void;
};

type ToggleEvent = React.MouseEvent | React.KeyboardEvent | null;

type UseMobileMenuToggleResult = {
  showNav: boolean;
  isClosingMenu: boolean;
  setShowNav: (isOpen: boolean) => void;
  onToggleMenu: (event?: ToggleEvent, forceClose?: boolean) => void;
};

export const useMobileMenuToggle = ({
  updateMobileNavState,
}: UseMobileMenuToggleParams): UseMobileMenuToggleResult => {
  const [showNav, setShowNavState] = useState(false);
  const [isClosingMenu, setIsClosingMenu] = useState(false);
  const showNavRef = useRef(false);
  const closeTimerRef = useRef<number | null>(null);
  const closeAnimationDurationMs = 500;

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  useEffect(
    function cleanupCloseTimerOnUnmount() {
      return function clearTimer() {
        clearCloseTimer();
      };
    },
    [clearCloseTimer]
  );

  const setShowNav = useCallback((isOpen: boolean) => {
    setShowNavState(isOpen);
    showNavRef.current = isOpen;
  }, []);

  const handleDeviceOrientation = useCallback(() => {
    if (isMin1280pxWide()) {
      updateMobileNavState(false);
      window.document.body.classList.remove('lockBody');
    }

    clearCloseTimer();
    setIsClosingMenu(false);
    setShowNav(false);
  }, [clearCloseTimer, setShowNav, updateMobileNavState]);

  useEffect(
    function watchDeviceOrientationChange() {
      const mediaQuery = min1280pxWideMediaQuery();
      handleDeviceOrientation();

      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleDeviceOrientation);
      } else {
        mediaQuery.addListener(handleDeviceOrientation);
      }

      return function unwatchDeviceOrientationChange() {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', handleDeviceOrientation);
        } else {
          mediaQuery.removeListener(handleDeviceOrientation);
        }
      };
    },
    [handleDeviceOrientation]
  );

  const onToggleMenu = useCallback(
    (event: ToggleEvent = null, forceClose = false) => {
      const nextShowNav = forceClose ? false : !showNavRef.current;
      const isClosingFromOpen = showNavRef.current && !nextShowNav;

      event?.preventDefault();

      dispatchNewEvent('dataStateChange', {
        action: 'topNav - Side Flyout',
        type: showNavRef.current ? 'Closed' : 'Open',
      });

      if (showNavRef.current) {
        sendEvent('mobile-menu-click.menu-close');
      }

      clearCloseTimer();

      if (isClosingFromOpen) {
        setIsClosingMenu(true);
        closeTimerRef.current = window.setTimeout(() => {
          setIsClosingMenu(false);
          closeTimerRef.current = null;
        }, closeAnimationDurationMs);
      } else {
        setIsClosingMenu(false);
      }

      setShowNav(nextShowNav);
      updateMobileNavState(nextShowNav);

      const menuClick = new CustomEvent(
        'jl-events.browser.mobileHeaderMenuClick',
        {
          detail: {
            hide: nextShowNav,
          },
        }
      );

      window.dispatchEvent(menuClick);

      if (nextShowNav) {
        window.document.body.classList.add('lockBody');
      } else {
        window.document.body.classList.remove('lockBody');
      }
    },
    [clearCloseTimer, setShowNav, updateMobileNavState]
  );

  return {
    showNav,
    isClosingMenu,
    setShowNav,
    onToggleMenu,
  };
};
